/**
 * Canon retrieval — the Canon packet source (Slice 1 part 2).
 *
 * Fetches the generated canon-sections corpus (public/ai/canon-sections.json,
 * built from docs/1-canon by scripts/buildCanonSections.ts) once per session
 * and retrieves the top-scoring sections for a query, deterministically.
 * The model never fetches; packet assembly does (design brief §4 rule 1).
 */

import { scoreSection, tokenizeQuery, type CanonSection } from './canonSectionsCore';

export interface CanonHit {
  ref: string;
  docTitle: string;
  heading: string;
  excerpt: string;
}

interface CanonCorpus {
  schema: string;
  version: string;
  sections: CanonSection[];
}

let corpusPromise: Promise<CanonCorpus | null> | null = null;

async function loadCorpus(): Promise<CanonCorpus | null> {
  if (!corpusPromise) {
    corpusPromise = fetch('/ai/canon-sections.json')
      .then((r) => (r.ok ? (r.json() as Promise<CanonCorpus>) : null))
      .catch(() => null);
  }
  return corpusPromise;
}

/** Test seam. */
export function __resetCanonCorpusCache(): void {
  corpusPromise = null;
}

export const CANON_EXCERPT_CAP = 900;

/**
 * Top-k canon sections for a query. Returns [] (never throws) when the
 * corpus is unavailable — the packet then carries a named omission.
 */
export async function retrieveCanonSections(query: string, k = 3): Promise<CanonHit[]> {
  const corpus = await loadCorpus();
  if (!corpus?.sections?.length) return [];
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return [];

  const scored = corpus.sections
    .map((s) => ({ s, score: scoreSection(tokens, s) }))
    .filter((x) => x.score >= 3)
    .sort((a, b) => b.score - a.score)
    .slice(0, k);

  return scored.map(({ s }) => ({
    ref: s.ref,
    docTitle: s.docTitle,
    heading: s.heading,
    excerpt: s.text.length > CANON_EXCERPT_CAP ? `${s.text.slice(0, CANON_EXCERPT_CAP)}…` : s.text,
  }));
}
